import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Eye, Trash2, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useGlobalToast } from '@/contexts/ToastContext';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { sendDeletionEmail, sendCancellationEmail } from '@/services/emailService';

interface NotificationPreferences {
  email: {
    new_jobs: boolean;
    quote_responses: boolean;
    messages: boolean;
    reviews: boolean;
  };
}

interface PrivacySettings {
  profile_visible: boolean;
  show_phone_publicly: boolean;
  show_email_publicly: boolean;
  marketing_emails: boolean;
}

interface SecuritySettings {
  insurance_verified: boolean;
  certifications: string[];
  background_check_status: string;
  background_check_date: string | null;
}

interface Settings {
  notification_preferences: NotificationPreferences;
  privacy_settings: PrivacySettings;
  security_settings: SecuritySettings;
  account_status: string;
  deletion_requested_at?: string;
  scheduled_deletion_at?: string;
  days_until_deletion?: number;
}

type TabType = 'notifications' | 'privacy' | 'danger';

export function ProviderSettingsPage() {
  const navigate = useNavigate();
  const { success, error } = useGlobalToast();
  const [activeTab, setActiveTab] = useState<TabType>('notifications');
  const [settings, setSettings] = useState<Settings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [deleteReason, setDeleteReason] = useState('');

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://npm.rapidfix.ie';

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/providers/settings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else if (response.status === 500) {
        // Provider profile might not exist yet, use defaults
        console.warn('Provider settings not found, using defaults');
        setSettings({
          notification_preferences: {
            email: {
              new_jobs: true,
              quote_responses: true,
              messages: true,
              reviews: true
            }
          },
          privacy_settings: {
            profile_visible: true,
            show_phone_publicly: false,
            show_email_publicly: false,
            marketing_emails: true
          },
          security_settings: {
            insurance_verified: false,
            certifications: [],
            background_check_status: 'not_started',
            background_check_date: null
          },
          account_status: 'active'
        });
      } else {
        error('Load Failed', 'Could not load settings');
      }
    } catch (err) {
      console.error('Settings load error:', err);
      error('Load Failed', 'Could not load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/providers/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          notification_preferences: settings.notification_preferences,
          privacy_settings: settings.privacy_settings,
          security_settings: settings.security_settings
        })
      });

      if (response.ok) {
        success('Saved', 'Settings updated successfully');
      } else {
        error('Save Failed', 'Could not save settings');
      }
    } catch (err) {
      error('Save Failed', 'Could not save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    const userEmail = localStorage.getItem('user_email') || '';
    if (deleteConfirmEmail !== userEmail) {
      error('Email Mismatch', 'The email you entered does not match your account email');
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/providers/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          password: '',
          reason: deleteReason
        })
      });

      if (response.ok) {
        success('Deletion Requested', 'Your account will be deleted in 30 days');
        setShowDeleteModal(false);
        loadSettings();
        
        // Send confirmation email via Cloudflare Worker
        const userName = localStorage.getItem('user_name') || 'Provider';
        const token = localStorage.getItem('auth_token') || '';
        await sendDeletionEmail({
          email: userEmail,
          userName,
          daysRemaining: 30,
          token
        });
      } else {
        error('Request Failed', 'Could not request account deletion');
      }
    } catch (err) {
      error('Request Failed', 'Could not request account deletion');
    }
  };

  const handleCancelDeletion = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/auth/providers/cancel-deletion`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        success('Cancelled', 'Account deletion cancelled');
        loadSettings();
        
        // Send cancellation confirmation email via Cloudflare Worker
        const userEmail = localStorage.getItem('user_email') || '';
        const userName = localStorage.getItem('user_name') || 'Provider';
        const token = localStorage.getItem('auth_token') || '';
        await sendCancellationEmail({
          email: userEmail,
          userName,
          token
        });
      } else {
        error('Cancel Failed', 'Could not cancel deletion');
      }
    } catch (err) {
      error('Cancel Failed', 'Could not cancel deletion');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Settings Not Found</h2>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </Card>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Settings' }
  ];

  const tabs = [
    { id: 'notifications' as TabType, label: 'Notifications', icon: Bell },
    { id: 'privacy' as TabType, label: 'Privacy', icon: Eye },
    { id: 'danger' as TabType, label: 'Danger Zone', icon: Trash2 }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-6xl mx-auto px-4">
        <Breadcrumb items={breadcrumbItems} />

        <div className="mb-8 mt-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Settings</h1>
          <p className="text-gray-600">Manage your account preferences and security</p>
        </div>

        {/* Deletion Warning Banner */}
        {settings.account_status === 'pending_deletion' && (
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-bold text-red-900 mb-1">
                  Account Deletion Scheduled
                </h3>
                <p className="text-red-800 mb-3">
                  Your account is scheduled for deletion in {settings.days_until_deletion} days.
                </p>
                <Button onClick={handleCancelDeletion} className="bg-red-600 hover:bg-red-700">
                  Cancel Deletion
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <Card className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Notification Preferences</h2>
            <div className="space-y-4">
              {[
                { key: 'new_jobs', label: 'New Jobs', desc: 'Get notified about new jobs in your service area' },
                { key: 'quote_responses', label: 'Quote Responses', desc: 'When customers respond to your quotes' },
                { key: 'messages', label: 'Messages', desc: 'When customers send you messages' },
                { key: 'reviews', label: 'Reviews', desc: 'When customers leave reviews' }
              ].map((item) => (
                <label key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <div>
                    <div className="font-medium text-gray-900">{item.label}</div>
                    <div className="text-sm text-gray-600">{item.desc}</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.notification_preferences.email[item.key as keyof typeof settings.notification_preferences.email]}
                    onChange={(e) => setSettings({
                      ...settings,
                      notification_preferences: {
                        ...settings.notification_preferences,
                        email: {
                          ...settings.notification_preferences.email,
                          [item.key]: e.target.checked
                        }
                      }
                    })}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                  />
                </label>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t">
              <Button onClick={saveSettings} disabled={isSaving} fullWidth>
                {isSaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </Card>
        )}

        {/* Privacy Tab */}
        {activeTab === 'privacy' && (
          <Card className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Privacy Settings</h2>
            <div className="space-y-4">
              <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                <div>
                  <div className="font-medium text-gray-900">Profile Visibility</div>
                  <div className="text-sm text-gray-600">Show your profile in search results</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.privacy_settings.profile_visible}
                  onChange={(e) => setSettings({
                    ...settings,
                    privacy_settings: { ...settings.privacy_settings, profile_visible: e.target.checked }
                  })}
                  className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                />
              </label>

              <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                <div>
                  <div className="font-medium text-gray-900">Show Phone Number</div>
                  <div className="text-sm text-gray-600">Display phone number on public profile</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.privacy_settings.show_phone_publicly}
                  onChange={(e) => setSettings({
                    ...settings,
                    privacy_settings: { ...settings.privacy_settings, show_phone_publicly: e.target.checked }
                  })}
                  className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                />
              </label>

              <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                <div>
                  <div className="font-medium text-gray-900">Show Email</div>
                  <div className="text-sm text-gray-600">Display email on public profile</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.privacy_settings.show_email_publicly}
                  onChange={(e) => setSettings({
                    ...settings,
                    privacy_settings: { ...settings.privacy_settings, show_email_publicly: e.target.checked }
                  })}
                  className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                />
              </label>

              <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                <div>
                  <div className="font-medium text-gray-900">Marketing Emails</div>
                  <div className="text-sm text-gray-600">Receive tips, offers, and updates</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.privacy_settings.marketing_emails}
                  onChange={(e) => setSettings({
                    ...settings,
                    privacy_settings: { ...settings.privacy_settings, marketing_emails: e.target.checked }
                  })}
                  className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                />
              </label>
            </div>
            <div className="mt-6 pt-6 border-t">
              <Button onClick={saveSettings} disabled={isSaving} fullWidth>
                {isSaving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </Card>
        )}

        {/* Danger Zone Tab */}
        {activeTab === 'danger' && settings.account_status !== 'pending_deletion' && (
          <Card className="p-6 border-2 border-red-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-red-900">Danger Zone</h2>
                <p className="text-sm text-red-700">Irreversible actions</p>
              </div>
            </div>

            <div className="bg-red-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 mb-2">
                Once you delete your account, there is no going back. You have 30 days to cancel.
              </p>
              <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                <li>All your profile data will be deleted</li>
                <li>Your quotes and job history will be removed</li>
                <li>Customers won't be able to find you</li>
                <li>All reviews and ratings will be lost</li>
              </ul>
            </div>

            <Button
              onClick={() => setShowDeleteModal(true)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete My Account
            </Button>
          </Card>
        )}

        {/* Delete Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Card className="max-w-lg w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Delete Account</h2>
              </div>

              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  This will schedule your account for deletion in 30 days. You can cancel anytime before then.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type your email to confirm
                    </label>
                    <input
                      type="email"
                      value={deleteConfirmEmail}
                      onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for leaving (optional)
                    </label>
                    <textarea
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      rows={3}
                      placeholder="Help us improve..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleDeleteAccount}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={!deleteConfirmEmail}
                >
                  Delete Account
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmEmail('');
                    setDeleteReason('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
